// S5 — Captura por delegación del supervisor.
//
// Reutiliza el flow visual de L2 (refactor 2026-05-26 alineado a L2):
//   - SKU con Combobox de búsqueda
//   - CantidadInput con separador de miles es-CO
//   - Operarios obligatorio con default null y auto-fill por SKU
//   - Golpes/min obligatorio condicional según SKU.golpes_min_aplica
//   - OP/Lote obligatorio
//   - Toggle "¿Hubo paros NUEVOS?" Sí/No (gate UX)
//   - Bug fix de combinarFechaHora usando fecha_hoy del servidor
//
// Diferencias específicas del S5 (NO del L2):
//   - Header con tono warn (banner amber) — marca claramente que es delegación
//   - Card "Razón de la delegación" obligatoria ≥20 chars (validación dura)
//   - capturado_por_supervisor=true + lider_asignado_user_id del contexto
//   - Sin opción "fuera de turno" (delegación siempre dentro del turno actual)
//   - Sin banner paros sin cerrar del turno (esos son del líder titular —
//     scope distinto)
//
// Pre-requisito (verificado en S2 antes de mostrar el botón "Capturar por
// delegación"): la línea debe tener `programacion_lider_hoy.activa: true`
// (hay líder a quien delegar). El backend valida scope (planta∈scope del
// supervisor) y autorización runtime (líder programado).

import { useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import type { CausalParo, CausalParoTreeNode } from 'schemas';
import { combinarFechaHoraTurno } from 'schemas';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Combobox, type ComboboxOption } from '../../components/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { SupervisorSidebar } from '../../components/layout/SupervisorSidebar';
import { Textarea } from '../../components/ui/textarea';
import { ApiError } from '../../lib/api/client';
import {
  useCausalesParo,
  useCrearRegistro,
  useSkusByLinea,
  useSupervisorLineaDetalle,
} from '../../lib/api/queries';
import { useAuth } from '../../lib/auth';
import { useTenantConfig } from '../../lib/api/useTenantConfig';
import { cn } from '../../lib/utils';
import { limpiarEntradaLote, propsInputLote, validarLoteUi } from '../../lib/formatoLote';

// =============================================================================
// Form schema interno
// =============================================================================

const formParoSchema = z.object({
  causal_madre_id: z.string().uuid('Elija una categoría'),
  causal_id: z.string().uuid('Elija una causal'),
  hora_inicio: z.string().min(1, 'Requerido'),
  hora_fin: z.string().nullable(),
  observaciones: z.string().nullable(),
  requiere_observacion: z.boolean().default(false),
});
type FormParo = z.infer<typeof formParoSchema>;

// FormValues con campos nullable — la validación final ocurre en onSubmit
// antes del POST (mismo patrón que L2 — fuerza al supervisor a TOCAR cada
// campo conscientemente).
interface FormValues {
  sku_id: string;
  cantidad_procesada: number | null;
  numero_operarios: number | null;
  golpes_por_minuto: number | null;
  op_lote_oc_cliente: string;
  razon_delegacion: string;
  paros: FormParo[];
  paros_estado: 'si' | 'no' | null;
}

// =============================================================================
// Componente
// =============================================================================

export function S5CapturaDelegacionPage() {
  const { lineaId } = useParams<{ lineaId: string }>();
  const { state } = useAuth();
  const { atribucionCruce } = useTenantConfig();
  const navigate = useNavigate();
  const contexto = useSupervisorLineaDetalle(lineaId);
  const skusQ = useSkusByLinea(lineaId);
  const causalesQ = useCausalesParo();
  const crearMut = useCrearRegistro();
  const [paroModalOpen, setParoModalOpen] = useState(false);

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  if (contexto.isLoading || skusQ.isLoading || causalesQ.isLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 p-6">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-tertiary">Cargando…</p>
      </main>
    );
  }

  if (contexto.isError || !contexto.data) {
    const isForbidden = contexto.error instanceof ApiError && contexto.error.status === 403;
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-lg font-medium text-crit">
          {isForbidden ? 'Línea fuera de su scope' : 'No pudimos cargar el contexto'}
        </h1>
        <Button onClick={() => navigate('/supervisor')}>Volver al dashboard</Button>
      </main>
    );
  }

  const { linea, turno_objetivo, programacion_lider_hoy } = contexto.data;

  // Gate: solo se puede delegar si hay líder asignado para HOY en este turno
  if (!programacion_lider_hoy.activa || !programacion_lider_hoy.lider || !turno_objetivo.id) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-lg font-medium text-fg">No se puede delegar en esta línea</h1>
        <p className="text-sm text-fg-secondary">
          {turno_objetivo.fuente === 'sin_turno'
            ? 'No hay turno activo en este momento.'
            : 'Esta línea no tiene líder programado para el turno actual. La delegación requiere un líder asignado.'}
        </p>
        <Button variant="outline" onClick={() => navigate(`/supervisor/linea/${lineaId}`)}>
          Volver al detalle
        </Button>
      </main>
    );
  }

  return (
    <CapturaDelegacionForm
      lineaInfo={linea}
      turno={turno_objetivo}
      lider={programacion_lider_hoy.lider}
      fechaHoy={contexto.data.fecha_hoy}
      horaServidor={contexto.data.hora_servidor}
      skus={skusQ.data ?? []}
      causales={causalesQ.data ?? []}
      paroModalOpen={paroModalOpen}
      setParoModalOpen={setParoModalOpen}
      isSubmitting={crearMut.isPending}
      onCancelar={() => navigate(`/supervisor/linea/${lineaId}`)}
      onSubmit={async (values) => {
        // Fuente de verdad: backend con SET LOCAL TimeZone del tenant.
        // fecha + bloque desde el servidor, no el browser.
        const horaServidorHH = Number(contexto.data!.hora_servidor.slice(0, 2));
        const horaInicio = `${String(horaServidorHH).padStart(2, '0')}:00`;
        const horaFin = `${String((horaServidorHH + 1) % 24).padStart(2, '0')}:00`;

        // Defensiva — los campos null ya pasaron por validación del form, TS lo
        // pierde acá. Toast + return en lugar de crashear.
        if (values.cantidad_procesada === null || values.cantidad_procesada <= 0) {
          toast.error('Cantidad procesada inválida');
          return;
        }
        if (values.numero_operarios === null) {
          toast.error('Operarios inválido');
          return;
        }

        try {
          await crearMut.mutateAsync({
            // Fecha OPERATIVA del turno (no fecha_hoy): en la cola
            // post-medianoche del T3 difieren — con fecha_hoy el registro
            // caía en la instancia equivocada y not_programmed daba 403.
            fecha: contexto.data!.fecha_operativa,
            turno_id: turno_objetivo.id!,
            linea_id: linea.id,
            // Bloque de 1 tramo (multi-sku-por-hora, Lote 4): la delegación de
            // supervisor sigue siendo mono-SKU, sin UI de tramos — el bloque
            // contenedor con `tramos: [tramo]` es el mismo contrato que usa
            // ahora L2CapturaPage.tsx para el caso mono-SKU.
            tramos: [
              {
                hora_inicio_bloque: horaInicio,
                hora_fin_bloque: horaFin,
                sku_id: values.sku_id,
                cantidad_procesada: values.cantidad_procesada,
                cantidad_kg: null,
                golpes_por_minuto: values.golpes_por_minuto,
                numero_operarios: values.numero_operarios,
                op_lote_oc_cliente: values.op_lote_oc_cliente.trim(),
              },
            ],
            es_captura_fuera_turno: false,
            razon_fuera_turno: null,
            capturado_por_supervisor: true,
            razon_delegacion: values.razon_delegacion.trim(),
            lider_asignado_user_id: programacion_lider_hoy.lider!.id,
            origen_solicitud: 'programa',
            programado: true,
            paros: values.paros.map((p) => {
              // Turno-aware (medianoche): mismo fix que L2 — en la cola
              // post-medianoche del T3 la hora pertenece al día siguiente.
              const turnoParaFecha = {
                hora_inicio: turno_objetivo.hora_inicio ?? '00:00',
                cruza_medianoche: turno_objetivo.cruza_medianoche,
              };
              return {
                causal_madre_id: p.causal_madre_id,
                causal_id: p.causal_id,
                hora_inicio: combinarFechaHoraTurno(
                  contexto.data!.fecha_operativa,
                  p.hora_inicio,
                  turnoParaFecha,
                  atribucionCruce,
                ),
                hora_fin: p.hora_fin
                  ? combinarFechaHoraTurno(
                      contexto.data!.fecha_operativa,
                      p.hora_fin,
                      turnoParaFecha,
                      atribucionCruce,
                    )
                  : null,
                observaciones: p.observaciones,
                requiere_observacion: p.requiere_observacion,
              };
            }),
          });
          toast.success('Captura por delegación guardada', {
            description: `${formatearMiles(values.cantidad_procesada)} u en ${linea.nombre}`,
          });
          navigate(`/supervisor/linea/${lineaId}`);
        } catch (err) {
          if (err instanceof ApiError) {
            if (err.status === 403) toast.error('Sin permiso', { description: err.message });
            else if (err.status === 409)
              toast.error('Ya existe un registro para esta hora', { description: err.message });
            else toast.error('Error al guardar', { description: err.message });
          } else {
            toast.error('Error al guardar');
          }
        }
      }}
    />
  );
}

// =============================================================================
// Form interno
// =============================================================================

interface FormProps {
  lineaInfo: { id: string; codigo: string; nombre: string; planta: { nombre: string } };
  turno: {
    id: string | null;
    numero: number | null;
    hora_inicio: string | null;
    hora_fin: string | null;
  };
  lider: { id: string; nombre: string; apellido: string };
  fechaHoy: string;
  horaServidor: string;
  skus: Array<{
    id: string;
    codigo_cliente: string;
    descripcion: string;
    formato: string | null;
    capacidad_estandar_unidades_hora: number | null;
    capacidad_estandar_golpes_min: number | null;
    personas_estandar_turno: number | null;
    golpes_min_aplica: boolean;
  }>;
  causales: CausalParoTreeNode[];
  paroModalOpen: boolean;
  setParoModalOpen: (v: boolean) => void;
  isSubmitting: boolean;
  onSubmit: (values: FormValues) => Promise<void>;
  onCancelar: () => void;
}

function CapturaDelegacionForm({
  lineaInfo,
  turno,
  lider,
  fechaHoy,
  horaServidor,
  skus,
  causales,
  paroModalOpen,
  setParoModalOpen,
  isSubmitting,
  onSubmit,
  onCancelar,
}: FormProps) {
  const { formatoLote } = useTenantConfig();
  const inputLote = propsInputLote(formatoLote);
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      sku_id: '',
      cantidad_procesada: null,
      numero_operarios: null,
      golpes_por_minuto: null,
      op_lote_oc_cliente: '',
      razon_delegacion: '',
      paros: [],
      paros_estado: null,
    },
  });

  const {
    fields: paros,
    append: appendParo,
    remove: removeParo,
  } = useFieldArray({
    control,
    name: 'paros',
  });

  const selectedSkuId = watch('sku_id');
  const razonDelegacion = watch('razon_delegacion');
  const parosEstado = watch('paros_estado');
  const selectedSku = useMemo(
    () => skus.find((s) => s.id === selectedSkuId) ?? null,
    [skus, selectedSkuId],
  );

  // Auto-fill operarios estándar al elegir SKU (UX nicety). Solo si el campo
  // está vacío — no pisa edición manual del supervisor.
  useEffect(() => {
    if (selectedSku?.personas_estandar_turno && watch('numero_operarios') === null) {
      setValue('numero_operarios', selectedSku.personas_estandar_turno);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- watch() no es dep
  }, [selectedSku, setValue]);

  // Si el SKU activo NO usa golpes/min, forzar el campo a null (evita valor
  // fantasma de un SKU anterior; el backend rechazaría con golpes_not_applicable).
  useEffect(() => {
    if (selectedSku && !selectedSku.golpes_min_aplica) {
      setValue('golpes_por_minuto', null);
    }
  }, [selectedSku, setValue]);

  function handleSubmitForm(values: FormValues) {
    if (!selectedSku) {
      toast.error('Elija un SKU antes de guardar');
      return;
    }
    if (values.paros_estado === null) {
      toast.error('Indique si hubo paros antes de guardar');
      return;
    }
    if (values.paros_estado === 'si' && values.paros.length === 0) {
      toast.error('Agregue al menos un paro o cambie su respuesta a "No"');
      return;
    }
    if (values.razon_delegacion.trim().length < 20) {
      toast.error('Razón de delegación ≥20 caracteres');
      return;
    }
    void onSubmit(values);
  }

  // Hora del bloque (referencia visual del header)
  const horaServidorHH = Number(horaServidor.slice(0, 2));
  const horaInicioBloque = `${String(horaServidorHH).padStart(2, '0')}:00`;
  const horaFinBloque = `${String((horaServidorHH + 1) % 24).padStart(2, '0')}:00`;

  return (
    <RoleLayout sidebar={<SupervisorSidebar />} roleLabel="Supervisor · Captura delegación">
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
        {/* Banner warn: marca claramente que es captura por delegación */}
        <Card variant="warn-left">
          <CardContent className="flex flex-col gap-1 p-3 pt-3">
            <p className="text-[11px] font-medium uppercase tracking-[0.1em] text-warn">
              Captura por delegación
            </p>
            <h1 className="text-lg font-medium text-fg">
              {lineaInfo.nombre}{' '}
              <span className="text-fg-tertiary">
                · Bloque {horaInicioBloque}–{horaFinBloque}
              </span>
            </h1>
            <p className="text-xs text-fg-secondary">
              Delegando captura del líder{' '}
              <strong className="text-fg">
                {lider.nombre} {lider.apellido}
              </strong>{' '}
              · {lineaInfo.planta.nombre}
              {turno.numero !== null ? ` · Turno ${turno.numero}` : ''}
              {turno.hora_inicio && turno.hora_fin
                ? ` · ${turno.hora_inicio} — ${turno.hora_fin}`
                : ''}
            </p>
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit(handleSubmitForm)} className="flex flex-col gap-4" noValidate>
          {/* ============================================================ */}
          {/* Bloque de captura — mismos campos que L2                     */}
          {/* ============================================================ */}
          <Card>
            <CardHeader>
              <CardTitle>
                Bloque {horaInicioBloque}–{horaFinBloque}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {/* SKU — Combobox con búsqueda */}
              <Controller
                control={control}
                name="sku_id"
                rules={{ required: 'Elija un SKU' }}
                render={({ field }) => {
                  const opts: ComboboxOption[] = skus.map((s) => ({
                    value: s.id,
                    label: s.codigo_cliente,
                    description: `${s.descripcion}${s.formato ? ` · ${s.formato}` : ''}`,
                    searchText: `${s.codigo_cliente} ${s.descripcion} ${s.formato ?? ''}`,
                  }));
                  return (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="sku_id">SKU</Label>
                      <Combobox
                        id="sku_id"
                        options={opts}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Buscar por código o descripción…"
                        invalid={Boolean(errors.sku_id)}
                        emptyLabel="Sin coincidencias"
                      />
                      {errors.sku_id ? (
                        <p className="text-xs text-crit">{errors.sku_id.message as string}</p>
                      ) : null}
                      {selectedSku?.capacidad_estandar_unidades_hora ? (
                        <p className="text-xs text-fg-tertiary">
                          Capacidad estándar:{' '}
                          <strong>
                            {formatearMiles(selectedSku.capacidad_estandar_unidades_hora)}
                          </strong>
                          /h
                          {selectedSku.golpes_min_aplica &&
                          selectedSku.capacidad_estandar_golpes_min
                            ? ` · ${selectedSku.capacidad_estandar_golpes_min} golpes/min`
                            : ''}
                        </p>
                      ) : null}
                      {skus.length === 0 ? (
                        <p className="text-xs text-warn">
                          ⚠ No hay SKUs configurados para esta línea.
                        </p>
                      ) : null}
                    </div>
                  );
                }}
              />

              {/* Cantidad + Operarios */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Controller
                  control={control}
                  name="cantidad_procesada"
                  rules={{
                    required: 'Cantidad procesada es obligatoria',
                    validate: (v) => (v !== null && v > 0) || 'Debe ser mayor a 0',
                  }}
                  render={({ field }) => (
                    <CantidadInput
                      id="cantidad_procesada"
                      label="Cantidad procesada"
                      placeholder="Ej. 1.250"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.cantidad_procesada?.message as string | undefined}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="numero_operarios"
                  rules={{
                    required: 'Operarios es obligatorio',
                    validate: (v) => (v !== null && v >= 1 && v <= 20) || 'Entre 1 y 20',
                  }}
                  render={({ field }) => (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="numero_operarios">Operarios (1–20)</Label>
                      <Input
                        id="numero_operarios"
                        type="number"
                        inputMode="numeric"
                        min={1}
                        max={20}
                        step={1}
                        placeholder="Ej. 3"
                        aria-invalid={Boolean(errors.numero_operarios) || undefined}
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(e.target.value === '' ? null : Number(e.target.value))
                        }
                      />
                      {errors.numero_operarios ? (
                        <p className="text-xs text-crit">
                          {errors.numero_operarios.message as string}
                        </p>
                      ) : null}
                    </div>
                  )}
                />
              </div>

              {/* Golpes/min — obligatorio si SKU lo aplica */}
              <Controller
                control={control}
                name="golpes_por_minuto"
                rules={{
                  validate: (v) => {
                    if (!selectedSku) return true;
                    if (selectedSku.golpes_min_aplica && (v === null || v <= 0)) {
                      return 'Golpes/min es obligatorio para este SKU';
                    }
                    return true;
                  },
                }}
                render={({ field }) => {
                  const noAplica = selectedSku ? !selectedSku.golpes_min_aplica : false;
                  return (
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="golpes_por_minuto">
                        Golpes/min{noAplica ? ' (no aplica a este SKU)' : ''}
                      </Label>
                      <Input
                        id="golpes_por_minuto"
                        type="number"
                        inputMode="decimal"
                        min={1}
                        step={0.1}
                        disabled={noAplica}
                        placeholder={noAplica ? 'No aplica' : 'Ej. 80'}
                        aria-invalid={Boolean(errors.golpes_por_minuto) || undefined}
                        value={field.value ?? ''}
                        onChange={(e) =>
                          field.onChange(e.target.value === '' ? null : Number(e.target.value))
                        }
                      />
                      {errors.golpes_por_minuto ? (
                        <p className="text-xs text-crit">
                          {errors.golpes_por_minuto.message as string}
                        </p>
                      ) : null}
                    </div>
                  );
                }}
              />

              {/* OP / Lote — obligatorio */}
              <Controller
                control={control}
                name="op_lote_oc_cliente"
                rules={{
                  required: 'OP / Lote es obligatorio',
                  // Formato del lote de la empresa (B7), el mismo que aplica el API.
                  validate: (v) => validarLoteUi(v, formatoLote),
                }}
                render={({ field }) => (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="op_lote_oc_cliente">OP / Lote / OC Cliente</Label>
                    <Input
                      id="op_lote_oc_cliente"
                      type="text"
                      inputMode={inputLote.inputMode}
                      maxLength={inputLote.maxLength}
                      placeholder={inputLote.placeholder}
                      aria-invalid={Boolean(errors.op_lote_oc_cliente) || undefined}
                      value={field.value}
                      onChange={(e) =>
                        field.onChange(limpiarEntradaLote(e.target.value, formatoLote))
                      }
                    />
                    {errors.op_lote_oc_cliente ? (
                      <p className="text-xs text-crit">
                        {errors.op_lote_oc_cliente.message as string}
                      </p>
                    ) : null}
                  </div>
                )}
              />
            </CardContent>
          </Card>

          {/* ============================================================ */}
          {/* Paros en este bloque — toggle Sí/No                          */}
          {/* ============================================================ */}
          <Card>
            <CardHeader>
              <CardTitle>¿Hubo paros nuevos en este bloque?</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <Controller
                control={control}
                name="paros_estado"
                rules={{
                  validate: (v) => v !== null || 'Indique Sí o No antes de guardar',
                }}
                render={({ field }) => (
                  <div className="flex flex-col gap-1.5">
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => field.onChange('si')}
                        className={cn(
                          'flex min-h-11 items-center justify-center rounded border px-3 py-2 text-sm font-medium transition-colors',
                          field.value === 'si'
                            ? 'border-accent bg-accent text-accent-ink'
                            : 'border-border bg-surface-elevated text-fg hover:bg-surface-overlay',
                        )}
                      >
                        Sí, hubo paros
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          field.onChange('no');
                          setValue('paros', []);
                        }}
                        className={cn(
                          'flex min-h-11 items-center justify-center rounded border px-3 py-2 text-sm font-medium transition-colors',
                          field.value === 'no'
                            ? 'border-ok bg-ok text-white'
                            : 'border-border bg-surface-elevated text-fg hover:bg-surface-overlay',
                        )}
                      >
                        No, sin paros
                      </button>
                    </div>
                    {errors.paros_estado ? (
                      <p className="text-xs text-crit">{errors.paros_estado.message as string}</p>
                    ) : null}
                  </div>
                )}
              />

              {parosEstado === 'si' ? (
                <div className="flex flex-col gap-2 border-t border-border pt-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-fg">
                      Paros agregados ({paros.length})
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setParoModalOpen(true)}
                    >
                      + Agregar paro
                    </Button>
                  </div>
                  {paros.length === 0 ? (
                    <p className="text-xs text-warn">
                      Agregue al menos un paro o cambie su respuesta a "No".
                    </p>
                  ) : (
                    paros.map((p, i) => {
                      const causalNombre = findCausalNombre(causales, p.causal_id);
                      return (
                        <div
                          key={p.id}
                          className="flex items-center justify-between rounded border border-border px-3 py-2 text-sm"
                        >
                          <div>
                            <p className="font-medium">{causalNombre ?? 'Sin causal'}</p>
                            <p className="text-xs text-fg-tertiary">
                              {p.hora_inicio} → {p.hora_fin ?? 'abierto'}
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeParo(i)}
                          >
                            Quitar
                          </Button>
                        </div>
                      );
                    })
                  )}
                </div>
              ) : null}
            </CardContent>
          </Card>

          {/* ============================================================ */}
          {/* Razón de la delegación — específico del S5                   */}
          {/* ============================================================ */}
          <Card variant="warn-left">
            <CardHeader>
              <CardTitle>Razón de la delegación</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-1.5">
              <Controller
                control={control}
                name="razon_delegacion"
                rules={{
                  required: 'Razón de delegación obligatoria',
                  validate: (v) => v.trim().length >= 20 || 'Razón de delegación ≥20 caracteres',
                }}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    placeholder="Ej. Líder Jose ausente por reunión con coordinador"
                    aria-invalid={Boolean(errors.razon_delegacion) || undefined}
                  />
                )}
              />
              <p
                className={cn(
                  'text-xs',
                  (razonDelegacion?.trim().length ?? 0) >= 20 ? 'text-ok' : 'text-fg-tertiary',
                )}
              >
                {razonDelegacion?.trim().length ?? 0}/20 caracteres mínimos
              </p>
              {errors.razon_delegacion ? (
                <p className="text-xs text-crit">{errors.razon_delegacion.message as string}</p>
              ) : null}
            </CardContent>
          </Card>

          {/* Submit + Cancelar */}
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onCancelar} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" size="lg" disabled={isSubmitting} className="flex-1">
              {isSubmitting ? (
                <>
                  <Spinner className="border-white/30 border-t-white" /> Guardando…
                </>
              ) : (
                'Guardar captura'
              )}
            </Button>
          </div>
        </form>

        <ParoModal
          open={paroModalOpen}
          onClose={() => setParoModalOpen(false)}
          causales={causales}
          onAdd={(p) => {
            appendParo(p);
            setParoModalOpen(false);
          }}
        />
      </div>
    </RoleLayout>
  );
}

// =============================================================================
// ParoModal (idéntico al de L2 — duplicación controlada en MVP; en F1.5 se
// extrae a un componente shared)
// =============================================================================

function ParoModal({
  open,
  onClose,
  causales,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  causales: CausalParoTreeNode[];
  onAdd: (paro: FormParo) => void;
}) {
  const [causalMadreId, setCausalMadreId] = useState('');
  const [causalId, setCausalId] = useState('');
  const [horaInicio, setHoraInicio] = useState('');
  const [horaFin, setHoraFin] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [error, setError] = useState<string | null>(null);

  const madre = causales.find((m) => m.id === causalMadreId);
  const especificas = madre?.hijas ?? [];
  const causalSeleccionada = especificas.find((c) => c.id === causalId) ?? null;
  const requiereObs = causalSeleccionada?.requiere_observacion ?? false;

  function handleAdd() {
    setError(null);
    const result = formParoSchema.safeParse({
      causal_madre_id: causalMadreId,
      causal_id: causalId,
      hora_inicio: horaInicio,
      hora_fin: horaFin || null,
      observaciones: observaciones.trim() || null,
      requiere_observacion: requiereObs,
    });
    if (!result.success) {
      const firstErr = result.error.errors[0];
      setError(firstErr ? `${firstErr.path.join('.')}: ${firstErr.message}` : 'Datos inválidos');
      return;
    }
    if (requiereObs && (!observaciones || observaciones.trim().length < 5)) {
      setError('Observación obligatoria (mínimo 5 caracteres) para esta causal');
      return;
    }
    if (horaFin && horaFin <= horaInicio) {
      setError('Hora fin debe ser posterior a hora inicio');
      return;
    }
    onAdd(result.data);
    setCausalMadreId('');
    setCausalId('');
    setHoraInicio('');
    setHoraFin('');
    setObservaciones('');
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogHeader>
        <DialogTitle>Agregar paro</DialogTitle>
      </DialogHeader>
      <DialogContent className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="paro-madre">Categoría</Label>
          <Select
            id="paro-madre"
            value={causalMadreId}
            onChange={(e) => {
              setCausalMadreId(e.target.value);
              setCausalId('');
            }}
          >
            <option value="">— Elija —</option>
            {causales.map((m) => (
              <option key={m.id} value={m.id}>
                {m.nombre}
              </option>
            ))}
          </Select>
        </div>
        {especificas.length > 0 ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="paro-especifica">Causal específica</Label>
            <Select
              id="paro-especifica"
              value={causalId}
              onChange={(e) => setCausalId(e.target.value)}
            >
              <option value="">— Elija —</option>
              {especificas.map((c: CausalParo) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                  {c.requiere_observacion ? ' (req. observación)' : ''}
                </option>
              ))}
            </Select>
          </div>
        ) : null}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="paro-inicio">Hora inicio</Label>
            <Input
              id="paro-inicio"
              type="time"
              value={horaInicio}
              onChange={(e) => setHoraInicio(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="paro-fin">Hora fin (opcional)</Label>
            <Input
              id="paro-fin"
              type="time"
              value={horaFin}
              onChange={(e) => setHoraFin(e.target.value)}
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="paro-obs">Observaciones{requiereObs ? ' (obligatorio)' : ''}</Label>
          <Textarea
            id="paro-obs"
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
          />
        </div>
        {error ? <p className="rounded bg-crit-soft px-3 py-2 text-xs text-crit">{error}</p> : null}
      </DialogContent>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancelar
        </Button>
        <Button onClick={handleAdd}>Agregar</Button>
      </DialogFooter>
    </Dialog>
  );
}

// =============================================================================
// CantidadInput — input con separador de miles es-CO (mismo patrón que L2)
// =============================================================================

function CantidadInput({
  id,
  label,
  placeholder,
  value,
  onChange,
  error,
}: {
  id: string;
  label: string;
  placeholder?: string;
  value: number | null;
  onChange: (v: number | null) => void;
  error?: string;
}) {
  const [focused, setFocused] = useState(false);
  const display = focused
    ? value === null
      ? ''
      : String(value)
    : value === null
      ? ''
      : formatearMiles(value);

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder}
        aria-invalid={Boolean(error) || undefined}
        value={display}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => onChange(parsearMiles(e.target.value))}
      />
      {error ? <p className="text-xs text-crit">{error}</p> : null}
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function formatearMiles(n: number): string {
  return new Intl.NumberFormat('es-CO').format(n);
}

function parsearMiles(s: string): number | null {
  const clean = s.replace(/[^\d]/g, '');
  if (clean === '') return null;
  const n = Number(clean);
  return Number.isFinite(n) ? n : null;
}

function findCausalNombre(tree: CausalParoTreeNode[], causalId: string): string | null {
  for (const madre of tree) {
    if (madre.id === causalId) return madre.nombre;
    const h = madre.hijas.find((x) => x.id === causalId);
    if (h) return `${madre.nombre} → ${h.nombre}`;
  }
  return null;
}

// (combinarFechaHora se reemplazó por combinarFechaHoraTurno de schemas —
// versión medianoche-aware, mismo helper que usa L2.)
